import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneCall,
  CheckCircle,
  Clock,
  Target,
} from 'lucide-react';
import { SalesDashboard as SalesDashboardData } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const outcomeLabels: Record<string, string> = {
  connected: 'Terhubung',
  not_connected: 'Tidak terhubung',
  invalid: 'Nomor invalid',
  call_back_later: 'Minta dihubungi kembali',
  other: 'Lainnya',
};

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hari ini' },
  { value: 'week', label: 'Minggu ini' },
  { value: 'month', label: 'Bulan ini' },
  { value: 'custom', label: 'Custom' },
];

const SalesDashboard: React.FC = () => {
  const [data, setData] = useState<SalesDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { period?: string; startDate?: string; endDate?: string } = { period };
      if (period === 'custom' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const res = await api.getSalesDashboard(params);
      setData(res);
    } catch (err: any) {
      console.error('Error loading sales dashboard:', err);
      setError(err?.response?.data?.message || 'Gagal memuat dashboard.');
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate]);

  useEffect(() => {
    // For custom, only fetch when both dates are set
    if (period === 'custom' && (!startDate || !endDate)) return;
    load();
  }, [load, period, startDate, endDate]);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });

  const statusCards = data
    ? [
        { name: 'Open Leads', value: data.openLeads, icon: PhoneCall, color: 'bg-blue-500', description: 'Lead terkini yang masih terbuka' },
        { name: 'Sudah di-follow-up', value: data.followedUp, icon: CheckCircle, color: 'bg-green-500', description: 'Lead terkini yang sudah dihubungi' },
        { name: 'Belum di-follow-up', value: data.notFollowedUp, icon: Clock, color: 'bg-yellow-500', description: 'Lead terkini yang belum dihubungi' },
      ]
    : [];

  const outcomeEntries = Object.entries(data?.outcomeBreakdown || {});

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">Ringkasan aktivitas follow-up leads.</p>
        </div>
        <Link
          to="/leads"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
        >
          <PhoneCall className="h-4 w-4" />
          Lihat Leads
        </Link>
      </div>

      {/* Period Selector */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === opt.value
                  ? 'bg-white text-primary-700 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="py-1.5 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-gray-500 text-sm">s/d</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="py-1.5 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center items-center h-48">
          <LoadingSpinner size="lg" text="Memuat dashboard..." />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-600">{error}</div>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Backlog — current state */}
          <div>
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Status Leads (Terkini)</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {statusCards.map(card => (
                <div key={card.name} className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 rounded-md p-3 ${card.color}`}>
                        <card.icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">{card.name}</dt>
                          <dd className="text-2xl font-semibold text-gray-900">{card.value}</dd>
                          <dd className="text-xs text-gray-400 mt-1">{card.description}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Activity for selected period */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-primary-600" />
                  <h3 className="text-lg font-medium text-gray-900">Aktivitas Periode</h3>
                </div>
                {data.period && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {formatDate(data.period.start)} – {formatDate(data.period.end)}
                  </span>
                )}
              </div>

              <div className="mb-6 flex flex-wrap gap-4">
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-indigo-600 font-medium">Follow-up dalam periode</p>
                  <p className="text-3xl font-bold text-indigo-900 mt-1">{data.followUpsInPeriod}</p>
                </div>
                <Link to="/leads?showClosing=1" className="bg-emerald-50 rounded-lg p-4 hover:bg-emerald-100 transition-colors">
                  <p className="text-sm text-emerald-700 font-medium">Closing</p>
                  <p className="text-3xl font-bold text-emerald-900 mt-1">{data.closingInPeriod}</p>
                  <p className="text-xs text-emerald-700/70 mt-1 max-w-[16rem]">
                    Company yang bayar pertama kali di periode ini setelah dihubungi sales
                  </p>
                </Link>
              </div>

              <h4 className="text-sm font-medium text-gray-700 mb-3">Outcome Breakdown</h4>
              {outcomeEntries.length === 0 ? (
                <p className="text-sm text-gray-500">Belum ada data outcome.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {outcomeEntries.map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-600 leading-tight">{outcomeLabels[key] || key}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesDashboard;
